import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { Toast, ToastType } from "./Toast";
import { sfxToastSuccess, sfxToastError } from "../lib/soundEffects";
import { reportErrorToast } from "../lib/errorReporting";

interface ToastData {
  id: string;
  message: string;
  type: ToastType;
  icon?: string; // Custom icon/emoji for the toast
}

interface ToastContextType {
  showToast: (message: string, type: ToastType, icon?: string) => string; // Returns toast ID
  dismissToast: (id: string) => void;
  showActionToast: (actionName: string, emoji: string) => string; // Shows action toast with emoji
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((message: string, type: ToastType, icon?: string): string => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, message, type, icon }]);
    if (type === 'success') sfxToastSuccess();
    else if (type === 'error') {
      sfxToastError();
      // An error toast is a failure the USER saw — make sure ops see it too. Filtered by
      // `classifyClientIssue`, so expected gameplay validation stays silent. See
      // `reportErrorToast`: without this, handled 4xx failures left no trace in the logs.
      reportErrorToast(message);
    }
    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Convenience method for showing action-in-progress toast with emoji
  const showActionToast = useCallback((actionName: string, emoji: string): string => {
    return showToast(`${actionName}...`, "action", emoji);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast, showActionToast }}>
      {children}
      <div className="suikamon-toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            icon={toast.icon}
            onClose={() => dismissToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
